import { Injectable } from '@angular/core';
import { CanActivate, Router, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { LoggerService } from '../services/logger.service';

/**
* Route guard (Injectable) that protects application routes by checking for a stored user token and validating it with the server. If no valid token is found or token validation fails, the guard logs the reason and redirects the user to the login page with an optional returnUrl.
* @component
* @selector auth-guard
* @example
*   // Use in Angular route configuration:
*   { path: 'protected', canActivate: [AuthGuard], component: ProtectedComponent }
* @input none - This guard does not accept inputs (it reads authentication state from AuthService).
* @output none - This guard does not emit outputs; it returns boolean or Observable<boolean> from canActivate.
*
* Purpose:
*   - Prevent unauthorized access to routes by verifying a locally stored token and confirming it with the server via AuthService.validateToken().
* Dependencies:
*   - AuthService: provides user() to read stored user data and validateToken() to verify the token remotely.
*   - Router: used to navigate to the '/login' route and pass a returnUrl query parameter.
*   - LoggerService: used to record info, debug, warn, and error messages about guard decisions and redirections.
*
* Key methods:
*   - canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> | Promise<boolean> | boolean
*       * Verifies the presence of a user token from AuthService.user().
*       * If no token is present, logs the denial and calls redirectToLogin with the attempted URL.
*       * If a token exists, calls AuthService.validateToken() and:
*           - returns true and logs access granted when the response is valid,
*           - logs a warning and redirects to login if the validation response is invalid,
*           - catches errors, logs the error, redirects to login, and returns false.
*   - redirectToLogin(returnUrl: string): void (private)
*       * Logs the redirect action and navigates to '/login' with queryParams: { returnUrl }.
*
* Lifecycle hooks:
*   - None specific to Angular component lifecycle. The guard executes at route activation time via the canActivate method.
*/
@Injectable({
  providedIn: 'root'
})
export class AuthGuard implements CanActivate {

  constructor(
    private authService: AuthService,
    private router: Router,
    private logger: LoggerService
  ) {}

  /**
  * Checks whether the current route can be activated by verifying a stored user token and validating it with the server.
  * @example
  * anActivate(routeSnapshot, routerState)
  * true
  * @param {{ActivatedRouteSnapshot}} {{route}} - The route snapshot being accessed.
  * @param {{RouterStateSnapshot}} {{state}} - The current router state snapshot.
  * @returns {{Observable<boolean> | Promise<boolean> | boolean}} Returns true if access is granted; otherwise redirects to login and returns false (or an observable/promise resolving to false).
  **/
  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> | Promise<boolean> | boolean {
    
    // Check if user data exists in local storage
    const userData = this.authService.user();
    
    if (!userData || !userData.token) {
      this.logger.info('Access denied - No valid user token', { route: state.url }, 'AuthGuard');
      this.redirectToLogin(state.url);
      return false;
    }

    // Validate token with server
    return this.authService.validateToken().pipe(
      map((response) => {
        if (response && response.status) {
          this.logger.debug('Access granted - Valid token', { route: state.url }, 'AuthGuard');
          return true;
        }
        this.logger.warn('Access denied - Invalid token response', { route: state.url }, 'AuthGuard');
        this.redirectToLogin(state.url);
        return false;
      }),
      catchError((error) => {
        this.logger.error('Access denied - Token validation failed', { error, route: state.url }, 'AuthGuard');
        this.redirectToLogin(state.url);
        return of(false);
      })
    );
  }

  private redirectToLogin(returnUrl: string): void {
    this.logger.info('Redirecting to login', { returnUrl }, 'AuthGuard');
    this.router.navigate(['/login'], { 
      queryParams: { returnUrl } 
    });
  }
} 